import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Slider,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Pause,
  SkipPrevious,
  SkipNext,
} from '@mui/icons-material';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import COLORS from '../Utils/colors';
import { revokeBlobUrl } from '../Utils/blobUrlUtils';

interface VideoCompressorProps {
  file: File;
  onCompressed: (compressedFile: File, originalSize: number, compressedSize: number) => void;
  onCancel: () => void;
}

interface TrimSegment {
  start: number;
  end: number;
  duration: number;
}

const VideoCompressor: React.FC<VideoCompressorProps> = ({
  file,
  onCompressed,
  onCancel,
}) => {
  const [ffmpeg, setFfmpeg] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [compressionStatus, setCompressionStatus] = useState('');
  const [originalVideoUrl, setOriginalVideoUrl] = useState<string>('');
  const [compressedVideoUrl, setCompressedVideoUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimSegment, setTrimSegment] = useState<TrimSegment>({
    start: 0,
    end: 0,
    duration: 0,
  });
  const [timelineFrames, setTimelineFrames] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'start' | 'end' | 'segment' | null>(null);
  const [isSegmentDragging, setIsSegmentDragging] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);
  const [trimProgress, setTrimProgress] = useState(0);
  const [trimStatus, setTrimStatus] = useState('');
  const [videoInfo, setVideoInfo] = useState<{
    duration: number;
    size: number;
    resolution: string;
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState<string>('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const compressedVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragTypeRef = useRef<'start' | 'end' | 'segment' | null>(null);
  const trimSegmentRef = useRef<TrimSegment>({ start: 0, end: 0, duration: 0 });
  const videoInfoRef = useRef<typeof videoInfo>(null);
  const originalIsBlobUrlRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const rafIdRef = useRef<number | null>(null);
  const taskIdRef = useRef<number>(0);

  // Initialize FFmpeg
  useEffect(() => {
    isMountedRef.current = true;
    const initFFmpeg = async () => {
      try {
        setCompressionStatus('🔄 Loading FFmpeg...');
        console.log('Starting FFmpeg initialization...');
        
        const ffmpegInstance = new FFmpeg();
        console.log('FFmpeg instance created');

        ffmpegInstance.on('log', ({ message }) => {
          console.log('FFmpeg Log:', message);
        });

        // Try loading with default configuration first
        try {
          console.log('Trying to load FFmpeg with default configuration...');
          await ffmpegInstance.load();
          console.log('Successfully loaded FFmpeg with default configuration');
          setFfmpeg(ffmpegInstance);
          setCompressionStatus('✅ FFmpeg loaded successfully');
          return;
        } catch (defaultError) {
          console.warn('Default configuration failed:', defaultError);
        }

        // If default fails, try with explicit URLs
        console.log('Trying with explicit CDN URLs...');
        await ffmpegInstance.load({
          coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.js',
          wasmURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/ffmpeg-core.wasm',
        });

        setFfmpeg(ffmpegInstance);
        setCompressionStatus('✅ FFmpeg loaded successfully');
        console.log('FFmpeg loaded successfully');
      } catch (err) {
        console.error('FFmpeg load error:', err);
        setError(`❌ Failed to load FFmpeg: ${err instanceof Error ? err.message : 'Unknown error'}. This might be due to network issues or CORS restrictions. Please try refreshing the page or check your internet connection.`);
      }
    };

    initFFmpeg();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load video info and start compression automatically
  useEffect(() => {
    const loadVideoInfoAndCompress = async () => {
      if (!file) return;

      let url: string = '';
      try {
        if (typeof (file as unknown) === 'string') {
          url = file as unknown as string;
          originalIsBlobUrlRef.current = false;
        } else if (file instanceof Blob) {
          url = URL.createObjectURL(file);
          originalIsBlobUrlRef.current = true;
        } else {
          // Fallback attempt
          // @ts-ignore
          if (file && typeof (file as any).size === 'number') {
            // @ts-ignore
            url = URL.createObjectURL(file as any);
            originalIsBlobUrlRef.current = true;
          }
        }
      } catch (e) {
        console.error('Failed to create video URL', e);
        setError('Could not load the selected video.');
        return;
      }

      if (!url) {
        setError('Invalid video source.');
        return;
      }

      if (!isMountedRef.current) return;
      setOriginalVideoUrl(url);

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const size = file.size;
        const resolution = `${video.videoWidth}x${video.videoHeight}`;

        if (!isMountedRef.current) return;
        setVideoInfo({ 
          duration, 
          size, 
          resolution,
          width: video.videoWidth,
          height: video.videoHeight
        });

        // Set initial trim segment with max 30s duration
        const initialDuration = Math.min(30, duration);
        setTrimSegment({
          start: 0,
          end: initialDuration,
          duration: initialDuration,
        });

        // Generate timeline frames
        generateTimelineFrames(video, duration);

        // Commented out duration check to allow longer videos
        // if (duration > maxDuration) {
        //   setError(`Video is ${duration.toFixed(1)}s long. Maximum allowed duration is ${maxDuration}s.`);
        //   return;
        // }

        // Manual processing - don't auto-start
        // if (ffmpeg) {
        //   setTimeout(() => {
        //     trimVideo();
        //   }, 500); // Small delay to ensure UI is ready
        // }
      };

      video.onerror = () => {
        const mediaError = (video as any).error as MediaError | undefined;
        const code = mediaError?.code;
        const map: Record<number, string> = {
          1: 'Aborted',
          2: 'Network error',
          3: 'Decode error (unsupported or corrupted file)',
          4: 'Source not supported',
        };
        if (!isMountedRef.current) return;
        setError(`Video load error: ${code ? map[code] || 'Unknown' : 'Unknown'}`);
        console.error('Video element error:', mediaError);
      };

      video.src = url;
    };

    loadVideoInfoAndCompress();
  }, [file]);

  // Revoke created object URLs when no longer needed to avoid memory leaks,
  // but never immediately after setting them as a <video src>.
  useEffect(() => {
    return () => {
      if (originalIsBlobUrlRef.current && originalVideoUrl) {
        revokeBlobUrl(originalVideoUrl);
      }
    };
  }, [originalVideoUrl]);

  useEffect(() => {
    return () => {
      revokeBlobUrl(compressedVideoUrl);
    };
  }, [compressedVideoUrl]);

  const getCompressionCommand = () => {
    // Detect video orientation and set appropriate scaling
    const isPortrait = videoInfo && videoInfo.height > videoInfo.width;
    let scaleFilter: string;
    
    if (isPortrait) {
      // For portrait videos, maintain portrait orientation
      scaleFilter = 'scale=720:1280:force_original_aspect_ratio=decrease,pad=720:1280:(ow-iw)/2:(oh-ih)/2';
    } else {
      // For landscape videos, maintain landscape orientation
      scaleFilter = 'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2';
    }
    
    console.log('Video orientation:', isPortrait ? 'Portrait' : 'Landscape');
    console.log('Scale filter:', scaleFilter);
    
    return [
      '-i', 'input.mp4',
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-crf', '25',
      '-b:v', '800k',
      '-maxrate', '800k',
      '-bufsize', '800k',
      '-vf', scaleFilter,
      '-c:a', 'aac',
      '-b:a', '128k',
      '-ar', '44100',
      '-movflags', '+faststart',
      '-profile:v', 'baseline',
      '-level', '3.1',
      '-x264opts', 'ref=1:bframes=0',
      '-threads', '0',
      'output.mp4'
    ];
  };

  // Generate timeline preview frames
  const generateTimelineFrames = async (video: HTMLVideoElement, duration: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const frameCount = 20;
    const frames: string[] = [];

    for (let i = 0; i < frameCount; i++) {
      const time = (duration / frameCount) * i;
      video.currentTime = time;
      
      await new Promise<void>((resolve) => {
        video.onseeked = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const frameDataUrl = canvas.toDataURL('image/jpeg', 0.3);
          frames.push(frameDataUrl);
          resolve();
        };
      });
    }

    if (!isMountedRef.current) return;
    setTimelineFrames(frames);
  };

  // Video playback controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      // Ensure playback starts within the selected segment
      const seg = trimSegmentRef.current;
      if (seg) {
        if (videoRef.current.currentTime < seg.start || videoRef.current.currentTime > seg.end) {
          videoRef.current.currentTime = seg.start;
        }
      }
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seekTo = (time: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const skipToStart = () => seekTo(trimSegment.start);
  const skipToEnd = () => seekTo(trimSegment.end);

  // Timeline drag handlers
  const handleTimelineMouseDown = (e: React.MouseEvent, type: 'start' | 'end' | 'segment') => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);
    isDraggingRef.current = true;
    dragTypeRef.current = type;
    if (type === 'segment') {
      setIsSegmentDragging(true);
    }
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
  };

  const updateFromClientX = (clientX: number) => {
    if (!isDraggingRef.current || !timelineRef.current || !videoInfoRef.current) return;

    const doUpdate = () => {
      const rect = timelineRef.current!.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const time = percentage * (videoInfoRef.current?.duration || 0);

      const currentDragType = dragTypeRef.current;
      const currentTrim = trimSegmentRef.current;
      const currentDuration = videoInfoRef.current?.duration || 0;

      const MAX_DURATION = 30;

      if (currentDragType === 'start') {
        const newStartRaw = Math.max(0, Math.min(time, currentTrim.end - 1));
        const desiredDuration = Math.min(currentTrim.end - newStartRaw, MAX_DURATION);
        const newStart = currentTrim.end - desiredDuration;
        setTrimSegment(prev => ({ ...prev, start: newStart, duration: desiredDuration }));
      } else if (currentDragType === 'end') {
        const newEndRaw = Math.min(currentDuration, Math.max(time, currentTrim.start + 1));
        const desiredDuration = Math.min(newEndRaw - currentTrim.start, MAX_DURATION);
        const newEnd = currentTrim.start + desiredDuration;
        setTrimSegment(prev => ({ ...prev, end: newEnd, duration: desiredDuration }));
      } else if (currentDragType === 'segment') {
        const segDuration = Math.min(currentTrim.duration, MAX_DURATION);
        const halfDuration = segDuration / 2;
        const newStart = Math.max(0, Math.min(currentDuration - segDuration, time - halfDuration));
        const newEnd = newStart + segDuration;
        setTrimSegment({ start: newStart, end: newEnd, duration: segDuration });
      }
      rafIdRef.current = null;
    };

    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(doUpdate);
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    e.preventDefault();
    updateFromClientX(e.clientX);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
    setIsSegmentDragging(false);
    isDraggingRef.current = false;
    dragTypeRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove as any);
    document.removeEventListener('mouseup', handleMouseUp as any);
    document.removeEventListener('touchmove', handleTouchMove as any);
    document.removeEventListener('touchend', handleTouchEnd as any);
  }, [handleMouseMove]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches && e.touches[0]) {
      e.preventDefault();
      updateFromClientX(e.touches[0].clientX);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    handleMouseUp();
  }, [handleMouseUp]);

  // Keep refs in sync with state to avoid stale closures during document events
  useEffect(() => {
    trimSegmentRef.current = trimSegment;
  }, [trimSegment]);

  useEffect(() => {
    videoInfoRef.current = videoInfo;
  }, [videoInfo]);

  // Update current time during playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  // Enforce playback within selected segment [start, end]
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleSegmentBounds = () => {
      const seg = trimSegmentRef.current;
      if (!seg) return;
      if (video.currentTime < seg.start) {
        video.currentTime = seg.start;
      } else if (video.currentTime > seg.end) {
        // Loop back to start when passing end
        video.currentTime = seg.start;
        if (!video.paused) {
          // keep playing within the loop
          void video.play();
        }
      }
    };

    const handleEnded = () => {
      const seg = trimSegmentRef.current;
      if (!seg) return;
      video.currentTime = seg.start;
      void video.play();
    };

    video.addEventListener('timeupdate', handleSegmentBounds);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('timeupdate', handleSegmentBounds);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  // If segment changes while playing, snap into bounds
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      const seg = trimSegment;
      if (video.currentTime < seg.start || video.currentTime > seg.end) {
        video.currentTime = seg.start;
      }
    }
  }, [trimSegment, isPlaying]);

  // Trim and compress video in one process
  const trimVideo = async () => {
    if (!ffmpeg || !file || !videoInfo) return;

    setIsTrimming(true);
    setIsLoading(true);
    setTrimProgress(0);
    setProgress(0);
    setError('');
    setTrimStatus('Starting video processing...');
    const taskId = ++taskIdRef.current;

    try {
      // ========== STAGE 1: TRIM (0-50%) ==========
      
      // Write input file - (0-5%)
      setTrimStatus('Loading video file...');
      setTrimProgress(2);
      await ffmpeg.writeFile('input.mp4', await fetchFile(file));
      setTrimProgress(5);

      // Calculate trim parameters
      const startTime = trimSegment.start;
      const duration = Math.min(30, trimSegment.duration);

      // Set up progress monitoring for trimming
      let maxProgressSeen = 5;
      let progressInterval: NodeJS.Timeout | null = null;
      let isTrimComplete = false;

      // Listen for real FFmpeg progress events during trimming
      const trimProgressHandler = ({ progress }: { progress: number }) => {
        if (taskIdRef.current !== taskId || !isMountedRef.current || isTrimComplete) return;
        // Map progress from 5% to 40% (trimming stage)
        const mappedProgress = 5 + (progress * 35);
        if (mappedProgress > maxProgressSeen) {
          maxProgressSeen = mappedProgress;
          setTrimProgress(maxProgressSeen);
          setTrimStatus(`Trimming video... ${Math.round(maxProgressSeen)}%`);
        }
      };
      
      ffmpeg.on('progress', trimProgressHandler);

      // Simulate progress as fallback
      progressInterval = setInterval(() => {
        if (taskIdRef.current !== taskId || !isMountedRef.current || isTrimComplete) {
          if (progressInterval) clearInterval(progressInterval);
          return;
        }
        if (maxProgressSeen < 40) {
          maxProgressSeen = Math.min(40, maxProgressSeen + 0.8);
          setTrimProgress(maxProgressSeen);
          setTrimStatus(`Trimming video... ${Math.round(maxProgressSeen)}%`);
        }
      }, 300);

      // Run trim command
      setTrimStatus('Trimming video...');
      setTrimProgress(8);
      await ffmpeg.exec([
        '-i', 'input.mp4',
        '-ss', startTime.toString(),
        '-t', duration.toString(),
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-crf', '23',
        '-c:a', 'aac',
        '-avoid_negative_ts', 'make_zero',
        'trimmed_output.mp4'
      ]);

      // Clean up trim progress
      isTrimComplete = true;
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Finalize trim (40-50%)
      maxProgressSeen = Math.max(40, maxProgressSeen);
      setTrimProgress(maxProgressSeen);
      setTrimStatus(`Finalizing trim... ${Math.round(maxProgressSeen)}%`);
      
      const data = await ffmpeg.readFile('trimmed_output.mp4');
      setTrimProgress(45);
      
      const trimmedBlob = new Blob([data], { type: 'video/mp4' });
      const trimmedFile = new File([trimmedBlob], file.name.replace(/\.[^/.]+$/, '_trimmed.mp4'), {
        type: 'video/mp4',
      });
      
      setTrimProgress(50);
      setTrimStatus('Starting compression...');

      // ========== STAGE 2: COMPRESS (50-100%) ==========
      
      // Write trimmed file (50-55%)
      await ffmpeg.writeFile('trimmed_input.mp4', await fetchFile(trimmedFile));
      setTrimProgress(55);

      // Set up progress monitoring for compression
      let maxCompressProgress = 55;
      let isCompressComplete = false;

      // Listen for real FFmpeg progress events during compression
      const compressProgressHandler = ({ progress }: { progress: number }) => {
        if (taskIdRef.current !== taskId || !isMountedRef.current || isCompressComplete) return;
        // Map progress from 55% to 90% (compression stage)
        const mappedProgress = 55 + (progress * 35);
        if (mappedProgress > maxCompressProgress) {
          maxCompressProgress = mappedProgress;
          setTrimProgress(maxCompressProgress);
          setTrimStatus(`Compressing video... ${Math.round(maxCompressProgress)}%`);
        }
      };
      
      ffmpeg.on('progress', compressProgressHandler);

      // Simulate progress for compression
      progressInterval = setInterval(() => {
        if (taskIdRef.current !== taskId || !isMountedRef.current || isCompressComplete) {
          if (progressInterval) clearInterval(progressInterval);
          return;
        }
        if (maxCompressProgress < 90) {
          maxCompressProgress = Math.min(90, maxCompressProgress + 0.8);
          setTrimProgress(maxCompressProgress);
          setTrimStatus(`Compressing video... ${Math.round(maxCompressProgress)}%`);
        }
      }, 300);

      // Detect video orientation
      const isPortrait = videoInfo && videoInfo.height > videoInfo.width;
      let scaleFilter: string;
      
      if (isPortrait) {
        scaleFilter = 'scale=720:1280:force_original_aspect_ratio=decrease,pad=720:1280:(ow-iw)/2:(oh-ih)/2';
      } else {
        scaleFilter = 'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2';
      }

      // Run compression command
      setTrimStatus('Compressing video...');
      setTrimProgress(58);
      await ffmpeg.exec([
        '-i', 'trimmed_input.mp4',
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-crf', '25',
        '-b:v', '800k',
        '-maxrate', '800k',
        '-bufsize', '800k',
        '-vf', scaleFilter,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-ar', '44100',
        '-movflags', '+faststart',
        '-profile:v', 'baseline',
        '-level', '3.1',
        '-x264opts', 'ref=1:bframes=0',
        '-threads', '0',
        'compressed_output.mp4'
      ]);

      // Clean up compression progress
      isCompressComplete = true;
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      // Finalize compression (90-100%)
      maxCompressProgress = Math.max(90, maxCompressProgress);
      setTrimProgress(maxCompressProgress);
      setTrimStatus(`Finalizing compression... ${Math.round(maxCompressProgress)}%`);

      const compressedData = await ffmpeg.readFile('compressed_output.mp4');
      setTrimProgress(95);
      
      const compressedBlob = new Blob([compressedData], { type: 'video/mp4' });
      const compressedFile = new File([compressedBlob], trimmedFile.name.replace(/\.[^/.]+$/, '_compressed.mp4'), {
        type: 'video/mp4',
      });

      // Calculate size reduction
      const originalSize = file.size;
      const compressedSize = compressedFile.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

      setTrimProgress(100);
      setTrimStatus(`Complete! Size reduced by ${reduction}%`);

      // Set compressed video URL for preview
      const compressedUrl = URL.createObjectURL(compressedFile);
      setCompressedVideoUrl(compressedUrl);

      // Call callback
      onCompressed(compressedFile, originalSize, compressedSize);

    } catch (err) {
      setError(`Processing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Processing error:', err);
    } finally {
      if (taskIdRef.current === taskId && isMountedRef.current) {
        setIsTrimming(false);
        setIsLoading(false);
      }
    }
  };

  const compressTrimmedVideo = async (trimmedFile: File) => {
    if (!ffmpeg || !trimmedFile) return;

    setIsLoading(true);
    setProgress(0);
    setError('');
    setCompressionStatus('Starting compression...');
    const taskId = ++taskIdRef.current;

    try {
      // Write trimmed file
      setCompressionStatus('Loading trimmed video...');
      await ffmpeg.writeFile('trimmed_input.mp4', await fetchFile(trimmedFile));

      // Set up progress monitoring
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        if (taskIdRef.current !== taskId || !isMountedRef.current) return;
        setProgress(progress * 100);
        setCompressionStatus(`Compressing... ${Math.round(progress * 100)}%`);
      });

      // Run compression command with orientation preservation
      setCompressionStatus('Compressing video...');
      
      // Detect video orientation and set appropriate scaling
      const isPortrait = videoInfo && videoInfo.height > videoInfo.width;
      let scaleFilter: string;
      
      if (isPortrait) {
        // For portrait videos, maintain portrait orientation
        scaleFilter = 'scale=720:1280:force_original_aspect_ratio=decrease,pad=720:1280:(ow-iw)/2:(oh-ih)/2';
      } else {
        // For landscape videos, maintain landscape orientation
        scaleFilter = 'scale=1280:720:force_original_aspect_ratio=decrease,pad=1280:720:(ow-iw)/2:(oh-ih)/2';
      }
      
      
      await ffmpeg.exec([
        '-i', 'trimmed_input.mp4',
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-crf', '25',
        '-b:v', '800k',
        '-maxrate', '800k',
        '-bufsize', '800k',
        '-vf', scaleFilter,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-ar', '44100',
        '-movflags', '+faststart',
        '-profile:v', 'baseline',
        '-level', '3.1',
        '-x264opts', 'ref=1:bframes=0',
        '-threads', '0',
        'compressed_output.mp4'
      ]);

      // Read compressed file
      setCompressionStatus('Finalizing compression...');
      const data = await ffmpeg.readFile('compressed_output.mp4');
      const compressedBlob = new Blob([data], { type: 'video/mp4' });
      const compressedFile = new File([compressedBlob], trimmedFile.name.replace(/\.[^/.]+$/, '_compressed.mp4'), {
        type: 'video/mp4',
      });

      // Calculate size reduction
      const originalSize = file.size;
      const compressedSize = compressedFile.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

      setCompressionStatus(`Compression complete! Size reduced by ${reduction}%`);
      setProgress(100);

      // Set compressed video URL for preview
      const compressedUrl = URL.createObjectURL(compressedFile);
      setCompressedVideoUrl(compressedUrl);

      // Call callback
      onCompressed(compressedFile, originalSize, compressedSize);

    } catch (err) {
      setError(`Compression failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Compression error:', err);
    } finally {
      if (taskIdRef.current === taskId && isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const compressVideo = async () => {
    if (!ffmpeg || !file) return;

    setIsLoading(true);
    setProgress(0);
    setError('');
    setCompressionStatus('Starting compression...');

    try {
      // Write input file
      setCompressionStatus('Loading video file...');
      await ffmpeg.writeFile('input.mp4', await fetchFile(file));

      // Set up progress monitoring
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgress(progress * 100);
        setCompressionStatus(`Compressing... ${Math.round(progress * 100)}%`);
      });

      // Run compression
      setCompressionStatus('Compressing video...');
      await ffmpeg.exec(getCompressionCommand());

      // Read output file
      setCompressionStatus('Finalizing...');
      const data = await ffmpeg.readFile('output.mp4');
      const compressedBlob = new Blob([data], { type: 'video/mp4' });
      const compressedFile = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, '_compressed.mp4'), {
        type: 'video/mp4',
      });

      // Create preview URL
      const compressedUrl = URL.createObjectURL(compressedBlob);
      setCompressedVideoUrl(compressedUrl);

      // Calculate size reduction
      const originalSize = file.size;
      const compressedSize = compressedFile.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

      setCompressionStatus(`Compression complete! Size reduced by ${reduction}%`);
      setProgress(100);

      // Call callback with compressed file
      onCompressed(compressedFile, originalSize, compressedSize);

    } catch (err) {
      setError(`Compression failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Compression error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Simple Loader Overlay */}
      <Backdrop
        open={isTrimming || isLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1000,
          bgcolor: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {/* Video Preview */}
          {originalVideoUrl && (
            <Box sx={{ 
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <video
                src={originalVideoUrl}
                muted
                playsInline
                style={{ 
                  width: 200, 
                  height: 150,
                  objectFit: 'cover',
                  borderRadius: 8
                }}
              />
              {/* Overlay with progress */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={isTrimming || isLoading ? trimProgress : progress}
                    size={60}
                    thickness={4}
                    sx={{
                      color: '#fff',
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {Math.round(isTrimming || isLoading ? trimProgress : progress)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Status Text */}
          <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 400, px: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 500,
                mb: 2,
              }}
            >
              {(isTrimming || isLoading) && trimStatus 
                ? trimStatus 
                : isTrimming 
                  ? `Trimming Video... ${Math.round(trimProgress)}%` 
                  : `Compressing Video... ${Math.round(progress)}%`}
            </Typography>
            
            {/* Progress Bar */}
            <Box sx={{ width: '100%', mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={isTrimming || isLoading ? trimProgress : progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: '#fff',
                  },
                }}
              />
            </Box>
            
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                mt: 1,
              }}
            >
              {Math.round(isTrimming || isLoading ? trimProgress : progress)}% Complete
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <Box sx={{ 
        width: '100%', 
        height: '100%',
        maxWidth: { xs: '95%', sm: 400, md: 400 }, 
        maxHeight: '90vh',
        mx: 'auto',
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 },
          overflowX: 'hidden',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)',
          },
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            minWidth: 0,
            width: '100%'
          }}>
            {/* Header */}
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                <Typography variant="h6" component="h2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Video Compression
                </Typography>
                <IconButton onClick={onCancel} size="small" sx={{ flexShrink: 0 }}>
                  <Close />
                </IconButton>
              </Box>
            </Box>

            {/* Error Alert */}
            {error && (
              <Box>
                <Alert severity="error">
                  {error}
                </Alert>
              </Box>
            )}

            {/* Video Player */}
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Box sx={{ minWidth: 0, width: '100%' }}>
                {originalVideoUrl && (
                  <video
                    ref={videoRef}
                    src={originalVideoUrl}
                    preload="metadata"
                    crossOrigin="anonymous"
                    muted
                    playsInline
                    style={{ 
                      width: '100%', 
                      maxWidth: '100%',
                      maxHeight: 250, 
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      objectFit: 'contain'
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                )}
                
                {/* Video Controls */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
                  <IconButton 
                    onClick={togglePlay} 
                    size="medium"
                    sx={{ 
                      bgcolor: COLORS.primary, 
                      color: 'white',
                      '&:hover': { bgcolor: COLORS.burntOrange }
                    }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <IconButton 
                    onClick={skipToStart} 
                    size="medium"
                    sx={{ 
                      bgcolor: 'grey.200',
                      '&:hover': { bgcolor: 'grey.300' }
                    }}
                  >
                    <SkipPrevious />
                  </IconButton>
                  <IconButton 
                    onClick={skipToEnd} 
                    size="medium"
                    sx={{ 
                      bgcolor: 'grey.200',
                      '&:hover': { bgcolor: 'grey.300' }
                    }}
                  >
                    <SkipNext />
                  </IconButton>
                </Box>

                {/* Video Info */}
                {videoInfo && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, minWidth: 0, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 0 }}>
                        {videoInfo.resolution}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 0 }}>
                        {formatDuration(videoInfo.duration)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 0 }}>
                        {formatFileSize(videoInfo.size)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Timeline */}
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, mr: 1 }}>
                    Timeline - Drag handles to select segment
                  </Typography>
                  
                  {/* Format Selection */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      MP4
                    </Typography>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ width: '100%', height: 2, bgcolor: 'currentColor' }} />
                        <Box sx={{ width: '100%', height: 2, bgcolor: 'currentColor' }} />
                      </Box>
                    </IconButton>
                  </Box>
                </Box>
          
          <Box
            ref={timelineRef}
            sx={{
              position: 'relative',
              height: 80,
              width: '100%',
              maxWidth: '100%',
              bgcolor: 'grey.300',
              borderRadius: 2,
              overflow: 'hidden',
              cursor: isDragging ? 'grabbing' : 'grab',
              border: '1px solid #ddd',
              userSelect: 'none',
              touchAction: 'none',
            }}
            onMouseDown={(e) => handleTimelineMouseDown(e, 'segment')}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const mouseEvent = new MouseEvent('mousedown', {
                clientX: touch.clientX,
                clientY: touch.clientY,
              });
              handleTimelineMouseDown(mouseEvent as any, 'segment');
            }}
          >
            {/* Timeline Frames */}
            <Box sx={{ display: 'flex', height: '100%' }}>
              {timelineFrames.map((frame, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    backgroundImage: `url(${frame})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              ))}
            </Box>

            {/* Current Time Indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(currentTime / (videoInfo?.duration || 1)) * 100}%`,
                width: 2,
                bgcolor: 'white',
                zIndex: 3,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -4,
                  left: -3,
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderBottom: '8px solid white',
                }
              }}
            />

            {/* Trim Segment - Yellow Highlight */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.start / (videoInfo?.duration || 1)) * 100}%`,
                width: `${(trimSegment.duration / (videoInfo?.duration || 1)) * 100}%`,
                bgcolor: isSegmentDragging ? 'rgba(236, 15, 15, 0.4)' : 'rgba(0, 30, 164, 0.2)',
                border: `2px solid ${isSegmentDragging ? '#ff9800' : '#ffc107'}`,
                zIndex: 2,
                cursor: 'grab',
                '&:active': {
                  cursor: 'grabbing',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: isSegmentDragging 
                    ? 'linear-gradient(90deg, rgba(255,152,0,0.2) 0%, rgba(255,152,0,0.1) 100%)'
                    : 'linear-gradient(90deg, rgba(255,193,7,0.1) 0%, rgba(255,193,7,0.05) 100%)',
                },
                userSelect: 'none',
                touchAction: 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleTimelineMouseDown(e, 'segment');
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const touch = e.touches[0];
                const mouseEvent = new MouseEvent('mousedown', {
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                });
                handleTimelineMouseDown(mouseEvent as any, 'segment');
              }}
            />

            {/* Start Handle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.start / (videoInfo?.duration || 1)) * 100}%`,
                width: 12,
                bgcolor: '#ffc107',
                cursor: 'ew-resize',
                zIndex: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { 
                  bgcolor: '#ffb300',
                  width: 16,
                },
                '&:active': {
                  bgcolor: '#ffa000',
                  width: 18,
                },
                '&::before': {
                  content: '""',
                  width: 2,
                  height: '60%',
                  bgcolor: 'white',
                  borderRadius: 1,
                },
                transition: 'all 0.2s ease',
                userSelect: 'none',
                touchAction: 'none',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleTimelineMouseDown(e, 'start');
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const touch = e.touches[0];
                const mouseEvent = new MouseEvent('mousedown', {
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                });
                handleTimelineMouseDown(mouseEvent as any, 'start');
              }}
            />

            {/* End Handle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(trimSegment.end / (videoInfo?.duration || 1)) * 100}%`,
                width: 12,
                bgcolor: '#ffc107',
                cursor: 'ew-resize',
                zIndex: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { 
                  bgcolor: '#ffb300',
                  width: 16,
                },
                '&:active': {
                  bgcolor: '#ffa000',
                  width: 18,
                },
                '&::before': {
                  content: '""',
                  width: 2,
                  height: '60%',
                  bgcolor: 'white',
                  borderRadius: 1,
                },
                transition: 'all 0.2s ease',
                userSelect: 'none',
                touchAction: 'none',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                handleTimelineMouseDown(e, 'end');
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const touch = e.touches[0];
                const mouseEvent = new MouseEvent('mousedown', {
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                });
                handleTimelineMouseDown(mouseEvent as any, 'end');
              }}
            />

            {/* Time Labels Overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 24,
                bgcolor: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                px: 1,
                fontSize: '12px',
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ flex: 1 }}>
                {formatDuration(trimSegment.start)}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
                {formatDuration(trimSegment.duration)}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, textAlign: 'right' }}>
                {formatDuration(trimSegment.end)}
              </Typography>
            </Box>
          </Box>
                
                {/* Trim Info */}
                {/* <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Start
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDuration(trimSegment.start)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Duration
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {formatDuration(trimSegment.duration)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      End
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDuration(trimSegment.end)}
                    </Typography>
                  </Box>
                </Box> */}
              </Box>
            </Box>

            {/* Progress is now shown in the full-screen overlay */}

            {/* Compressed Video Preview */}
            {compressedVideoUrl && (
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2, minWidth: 0, width: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.dark' }}>
                    ✓ Compressed Video Preview
                  </Typography>
                  <video
                    ref={compressedVideoRef}
                    src={compressedVideoUrl}
                    controls
                    style={{ 
                      width: '100%', 
                      maxWidth: '100%',
                      maxHeight: 200, 
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Next Button */}
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Box
                onClick={() => {
                  if (!isTrimming && !isLoading && ffmpeg && !error) {
                    trimVideo();
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  px: 4,
                  py: 2,
                  bgcolor: COLORS.primary,
                  color: 'white',
                  borderRadius: 2,
                  cursor: isTrimming || isLoading || !ffmpeg || !!error ? 'not-allowed' : 'pointer',
                  opacity: isTrimming || isLoading || !ffmpeg || !!error ? 0.6 : 1,
                  '&:hover': {
                    bgcolor: isTrimming || isLoading || !ffmpeg || !!error ? COLORS.primary : COLORS.burntOrange,
                    transform: isTrimming || isLoading || !ffmpeg || !!error ? 'none' : 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                  boxShadow: 3,
                  boxSizing: 'border-box',
                }}
              >
                <Typography variant="h6" fontWeight="medium">
                  Process Video
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Hidden canvas for frame generation */}
          <canvas
            ref={canvasRef}
            width={160}
            height={90}
            style={{ display: 'none' }}
          />
        </Box>
      </Box>
    </>
  );
};

export default VideoCompressor;
